/* eslint-disable @typescript-eslint/no-explicit-any */
import { Fragment, useEffect, useRef, useState } from 'react'

import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuIndicator,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
  NavigationMenuViewport
} from '~/components/UI/shadcn/navigation-menu'

import TertiaryLink from '~/components/UI/Buttons/TertiaryLink/TertiaryLink'
import MarkDown from '~/components/Helpers/MarkDown'
import { MenuItem } from '../Items/MenuItem'
import { cn } from '~/lib/utils'
import Search from '../Items/Search'
import Promo from '../Items/Promo'
import SearchIcon from '../../icons/search-icon'

import { cva } from 'class-variance-authority'

const hoverColours = cva('', {
  variants: {
    colour: {
      chartreuse: 'hover:bg-char-40 data-[state=open]:bg-char-40',
      'ink-blue': 'hover:bg-blue-d-60 data-[state=open]:bg-blue-d-60',
      'mid-blue': 'hover:bg-blue-m-60 data-[state=open]:bg-blue-m-60',
      'light-blue': 'hover:bg-blue-l-60 data-[state=open]:bg-blue-l-60',
      'light-pink': 'hover:bg-pink-60 data-[state=open]:bg-pink-60',
      'light-aqua': 'hover:bg-green-60 data-[state=open]:bg-green-60',
      'light-purple': 'hover:bg-purple-60 data-[state=open]:bg-purple-60',
      'light-orange': 'hover:bg-orange-60 data-[state=open]:bg-orange-60',
      'light-grey': 'hover:bg-grey-m data-[state=open]:bg-grey-m',
      white: 'hover:bg-grey-l data-[state=open]:bg-grey-l'
    }
  },
  defaultVariants: {
    colour: 'white'
  }
})

const DesktopNav = ({ data, colour }: HeaderProps) => {
  const listRef = useRef<HTMLUListElement>(null)
  const [viewportStyle, setViewportStyle] = useState<{
    left: number
    width: number
  }>({
    left: 0,
    width: 0
  })
  const [activeValue, setActiveValue] = useState<string>('')
  const [searchValue, setSearchValue] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)

  const isDarkBg = ['ink-blue', 'mid-blue'].includes(colour ?? '')
  const textColour = isDarkBg ? 'text-white' : 'text-blue-d'
  const iconColour = isDarkBg ? 'white' : 'ink-blue'

  useEffect(() => {
    //  Early return if no active value or list ref
    if (!activeValue || !listRef.current) return

    const updateViewportStyle = () => {
      // Early return if no active value or list ref
      if (!listRef.current) return

      // Find the active menu item and content
      const menuItem = listRef.current.querySelector(
        `[data-value="${activeValue}"]`
      ) as HTMLElement

      const content = document.querySelector(
        '[data-content="true"]'
      ) as HTMLElement

      if (!menuItem || !content) return

      // Defer width calculation to ensure styles are applied
      requestAnimationFrame(() => {
        const computedStyle = getComputedStyle(content)
        const padding =
          parseFloat(computedStyle.paddingLeft) +
          parseFloat(computedStyle.paddingRight)

        // Content width including padding
        const contentWidth = content.getBoundingClientRect().width + padding / 3

        // Trigger and NavigationMenu dimensions
        const triggerRect = menuItem.getBoundingClientRect()
        const menuRect = listRef.current?.getBoundingClientRect()
        if (!menuRect) return

        // Default left alignment
        let left = triggerRect.left - menuRect.left

        // left = triggerRect.left - menuRect.left + triggerRect.width / 1.8 - contentWidth / 2

        // Ensure the content can expand beyond the left edge of the container
        left = Math.max(
          -menuRect.left,
          Math.min(left, menuRect.width - contentWidth)
        )

        // Update viewport style
        setViewportStyle({
          left,
          width: contentWidth
        })
      })
    }

    // MutationObserver for dynamic DOM changes
    const observer = new MutationObserver(() => {
      updateViewportStyle()
    })

    // Observe changes to the list
    observer.observe(document.body, { childList: true, subtree: true })

    // Immediate update on active value change
    updateViewportStyle()

    return () => observer.disconnect()
  }, [activeValue])

  return (
    <NavigationMenu className='relative' id='main-menu'>
      <NavigationMenuList className='space-x-[6px] pl-0' ref={listRef}>
        {Array.isArray(data) &&
          data.map((item, index) => (
            <Fragment key={`nav-item-${item.label}-${index}`}>
              {!item.link && (
                <NavDropdownItem
                  item={item}
                  setActiveValue={setActiveValue}
                  textColour={textColour}
                  colour={colour}
                />
              )}

              {/* Render direct link item */}
              {item.link && (
                <NavLinkItem
                  item={item}
                  textColour={textColour}
                  colour={colour}
                />
              )}
            </Fragment>
          ))}

        {/* Search input */}
        <NavigationMenuItem data-value='Search' className='ml-s xl:ml-[38px]'>
          <NavigationMenuTrigger
            className={cn(
              hoverColours({ colour }),
              'px-2xs py-2xs group/search'
            )}
            onMouseEnter={() => setActiveValue('Search')}
            showChevron={false}
          >
            <SearchIcon
              fill={iconColour}
              secondaryfill={''}
              title={'Search'}
              width={24}
              height={24}
              isActive={false}
            />
          </NavigationMenuTrigger>

          <NavigationMenuContent
            data-content='true'
            className={cn('space-y-m lg:min-w-[600px]')}
          >
            <Search
              textColour=''
              onChange={e => setSearchValue(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
            />
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
      <NavigationMenuIndicator />
      <NavigationMenuViewport
        className='dark:text-blue-d absolute top-[calc(100%)] bg-white transition-[left,width] duration-150 ease-out will-change-auto dark:bg-white'
        style={{
          left: `calc(${viewportStyle.left}px - 20px)`,
          width: `calc(${viewportStyle.width}px + 20px)`
        }}
        onPointerLeave={event => {
          if (searchValue || searchFocused) {
            event.preventDefault()
          }
        }}
      />
    </NavigationMenu>
  )
}

const NavDropdownItem = ({
  item,
  setActiveValue,
  textColour,
  colour
}: {
  item: any
  setActiveValue: (value: string) => void
  textColour: string
  colour: AllBackgroundColours
}) => (
  <NavigationMenuItem data-value={item.label}>
    <NavigationMenuTrigger
      className={cn(
        textColour,
        hoverColours({ colour }),
        'px-2xs py-2xs-plus xl:px-xs'
      )}
      onMouseEnter={() => setActiveValue(item.label)}
      showChevron={true}
    >
      {item.label}
    </NavigationMenuTrigger>

    <NavigationMenuContent data-content='true'>
      <div
        className={cn(
          item.promo?.length > 0
            ? 'grid-cols-[275px_300px_250px]'
            : 'auto-cols-[300px]',
          'gap-xs-plus grid grid-flow-col'
        )}
      >
        {/* Promo content */}
        {item.promo?.length > 0 && (
          <Promo
            title={item.promo[0]?.title}
            description={item.promo[0]?.description}
            img={
              item.promo[0]?.img
                ? {
                    ...item.promo[0].img,
                    altText: item.promo[0].img.altText || ''
                  }
                : undefined
            }
            link={item.promo[0]?.link}
          />
        )}

        {/* Featured links */}
        {item.featured && (
          <div
            role='list'
            className={cn(
              'space-y-2xs list-none pl-0',
              item.featuredTwoCol &&
                'gap-xs-plus col-span-2 grid grid-cols-subgrid'
            )}
          >
            {item.featured.map((feat: any, index: number) => (
              <MenuItem
                href={feat.link?.sys?.uri}
                title={feat.label}
                key={`feat-${feat.label}-${index}`}
              >
                {feat.description && <MarkDown content={feat.description} />}
              </MenuItem>
            ))}
          </div>
        )}

        {/* Normal links */}
        {item.normal?.length > 0 && (
          <ul className='mt-2xs-plus space-y-m list-none pl-0'>
            {item.normal.map((normal: any, ind: number) => (
              <li className='w-fit' key={`normal-${ind}`}>
                <TertiaryLink
                  data={{
                    strongColour: false,
                    externalLink: normal.externalLink,
                    internalLink: normal.entry
                      ? {
                          sys: {
                            uri:
                              normal.entry?.sys?.uri +
                              (normal.query ? normal.query : '')
                          }
                        }
                      : undefined,
                    ariaLabel: normal.ariaLabel,
                    newWindow: normal.externalLink || normal.target,
                    label: normal.label
                  }}
                  colour={normal.colour}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </NavigationMenuContent>
  </NavigationMenuItem>
)

const NavLinkItem = ({
  item,
  textColour,
  colour
}: {
  item: any
  textColour: string
  colour: AllBackgroundColours
}) => (
  <li>
    <NavigationMenuLink
      className={cn(
        textColour,
        hoverColours({ colour }),
        navigationMenuTriggerStyle(),
        'unstyled-link px-2xs py-2xs-plus xl:px-xs bg-transparent'
      )}
      href={item.link.sys.uri}
    >
      {item.label}
    </NavigationMenuLink>
  </li>
)

export default DesktopNav
